import React, { useContext } from "react";
import {
  AlertCircle,
  Calendar,
  FileText,
  Home,
  MessageSquare,
  Package,
  Play,
  Ticket,
  Users,
  UsersRound,
} from "lucide-react";
import { useNavigate } from "react-router";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "../../../components/ui/card";
import { Button } from "../../../components/ui/button";
import { useTour } from "../../../components/ui/TourContext";
import AuthContext from "../../../hooks/authContext";
import { trackEvent, CMS_EVENTS } from "../../../services/apiservices/analytics";
import { useEffect } from "react";

const HelpTours = () => {
  const { startTour } = useTour();
  const navigate = useNavigate();
  const auth = useContext(AuthContext);

  useEffect(() => {
    trackEvent(CMS_EVENTS.HELP_OPENED, {
      user_id: auth?.userProfile?.id,
    });
  }, [auth?.userProfile?.id]);

  const waitForElement = (selector, callback, maxAttempts = 40) => {
    let attempts = 0;
    const checkElement = () => {
      const element = document.querySelector(selector);
      if (element) {
        callback();
      } else if (attempts < maxAttempts) {
        attempts++;
        setTimeout(checkElement, 100);
      } else {
        console.warn(
          `Element ${selector} not found after ${maxAttempts} attempts`
        );
      }
    };
    checkElement();
  };

  const tourSections = [
    {
      id: "getting-started",
      title: "Getting Started",
      description:
        "Learn the basics of navigating the platform and setting up your workspace.",
      icon: <Play className="h-6 w-6" />,
      available: true,
      action: () => {
        localStorage.setItem('startTour', 'getting-started');
        startTour("getting-started");
      },
    },
    {
      id: "dashboard",
      title: "Dashboard",
      description:
        "Understand your dashboard overview and key metrics at a glance.",
      icon: <Home className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate("/dashboard");
        localStorage.setItem('startTour', 'dashboard');
        waitForElement('[data-tour="overview-section"]', () => {
          startTour("dashboard");
        });
      },
    },
    {
      id: "appointments",
      title: "Appointments",
      description:
        "Master appointment scheduling, management, and client interactions.",
      icon: <Calendar className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'appointments');
         waitForElement('[data-tour="operations-nav"]', () => {
         startTour('appointments')
        });
      }
    },
    {
      id: "clients",
      title: "Clients",
      description:
        "Learn to manage client profiles, contacts, and relationship building.",
      icon: <Users className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'clients');
         waitForElement('[data-tour="people-nav"]', () => {
         startTour('clients')
        });
      }
    },
    {
      id: "teams",
      title: "Teams",
      description:
        "Organize your workforce, assign roles, and manage team groups.",
      icon: <UsersRound className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'employee');
         waitForElement('[data-tour="people-nav"]', () => {
         startTour('employee')
        });
      }
    },
    {
      id: "supplies",
      title: "Supplies",
      description:
        "Track inventory, manage requests, and handle supply chain operations.",
      icon: <Package className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'supplies');
         waitForElement('[data-tour="operations-nav"]', () => {
         startTour('supplies')
        });
      }
    },
    {
      id: "issues",
      title: "Issues",
      description:
        "Handle problem reporting, tracking, and resolution workflows.",
      icon: <AlertCircle className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'issues');
         waitForElement('[data-tour="operations-nav"]', () => {
         startTour('issues')
        });
      }
    },
    {
      id: "reports",
      title: "Reports",
      description: "Generate insights and analytics from your business data.",
      icon: <FileText className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'reports');
         waitForElement('[data-tour="report-nav"]', () => {
         startTour('reports')
        });
      }
    },
    {
      id: "message-board",
      title: "Message Board",
      description: "Communicate with your team through channels and live chat.",
      icon: <MessageSquare className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'messages');
         waitForElement('[data-tour="communications-nav"]', () => {
         startTour('messages')
        });
      }
    },
    {
      id: "support-accounts",
      title: "Support Accounts",
      description: "Manage customer support tickets and account assistance.",
      icon: <Ticket className="h-6 w-6" />,
      available: true,
      action: () => {
        navigate('/dashboard');
        localStorage.setItem('startTour', 'support');
         waitForElement('[data-tour="help-nav"]', () => {
         startTour('support')
        });
      }
    },
  ];

  const handleTourClick = (tour) => {
    if (tour?.available && tour?.action) {
      tour?.action();
    }
  };

  return (
    <div className="container mx-auto px-4 py-8 max-w-6xl dark:bg-[#1A1F2C]">
      <div className="mb-32">
        <h1 className="text-3xl font-bold text-gray-900 mb-8 dark:text-white">Guided Tours</h1>
        <p className="text-gray-600">
          Learn how to use each section of the platform with interactive guided
          tours.
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {tourSections?.map((tour) => (
          <Card
            key={tour.id}
            className={`transition-all duration-200 dark:bg-[#1A1F2C] dark:shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)] ${
              tour.available
                ? "hover:shadow-lg cursor-pointer"
                : "opacity-60 cursor-not-allowed"
            }`}
          >
            <CardHeader className="ph-24 pb-16 pt-24">
              <div className="flex items-center space-x-3">
                <div
                  className={`p-8 rounded-lg ${
                    tour.available
                      ? "bg-blue-50 text-blue-600"
                      : "bg-gray-100 text-gray-400"
                  }`}
                >
                  {tour.icon}
                </div>
                <div>
                  <CardTitle className="text-lg font-semibold">
                    {tour.title}
                  </CardTitle>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-0 ph-24 pb-24">
              <CardDescription className="text-sm text-gray-600 dark:text-gray-400 mb-16">
                {tour.description}
              </CardDescription>
              <Button
                onClick={() => handleTourClick(tour)}
                disabled={!tour.available}
                className={`w-full ${
                  tour.available
                    ? "bg-blue-600 hover:bg-blue-700 text-white"
                    : "bg-gray-200 text-gray-500 cursor-not-allowed"
                }`}
                variant={tour.available ? "default" : "secondary"}
              >
                <Play className="h-4 w-4 mr-8" />
                {tour.available ? "Start Tour" : "Coming Soon"}
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="mt-32 p-24 bg-blue-50 rounded-lg dark:bg-[#1A1F2C] shadow-md dark:shadow-[0_0_12px_rgba(2,8,23,0.8)]">
        <h3 className="font-semibold text-blue-900 mb-8">Need More Help?</h3>
        <p className="text-blue-700 text-sm">
          Can't find what you're looking for? Check out our documentation or
          contact support for additional assistance.
        </p>
      </div>
    </div>
  );
};

export default HelpTours;